import React from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { Trophy, ThumbsUp, TrendingUp, Compass } from "lucide-react";

const ACCENT_HEX = { indigo: "#818cf8", teal: "#2dd4bf", fuchsia: "#e879f9" };

export default function FinalScorecard({ scorecard, accent = "indigo" }) {
  const color = ACCENT_HEX[accent] || ACCENT_HEX.indigo;
  const chartData = [
    { metric: "Technical", value: scorecard.technical_accuracy },
    { metric: "Clarity", value: scorecard.clarity },
    { metric: "Structure", value: scorecard.structure },
    { metric: "Confidence", value: scorecard.confidence },
  ];

  return (
    <div className="space-y-5" data-testid="final-scorecard">
      <div className="glass-panel rounded-2xl p-8 text-center">
        <Trophy className="w-10 h-10 mx-auto mb-3" style={{ color }} />
        <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-bold">Overall Score</p>
        <p className="text-5xl font-heading font-bold text-white mt-1" data-testid="overall-score-value">{scorecard.overall_score}<span className="text-xl text-slate-500">/100</span></p>
        <div className="flex items-center justify-center gap-4 mt-4 text-xs text-slate-400">
          <span>{scorecard.avg_wpm || 0} avg WPM</span>
          <span>{scorecard.avg_filler_word_count || 0} avg filler words/answer</span>
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-6">
        <h3 className="font-heading font-semibold text-white mb-4 text-sm">Score Breakdown</h3>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
            <XAxis dataKey="metric" stroke="#94a3b8" fontSize={12} />
            <YAxis domain={[0, 100]} stroke="#94a3b8" fontSize={12} />
            <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} />
            <Bar dataKey="value" fill={color} radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div className="glass-panel rounded-2xl p-5" data-testid="scorecard-strengths">
          <h4 className="flex items-center gap-2 text-sm font-semibold text-emerald-400 mb-3"><ThumbsUp className="w-4 h-4" /> Strengths</h4>
          <ul className="space-y-2">
            {(scorecard.strengths || []).map((s, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-emerald-400">•</span>{s}</li>)}
          </ul>
        </div>
        <div className="glass-panel rounded-2xl p-5" data-testid="scorecard-weaknesses">
          <h4 className="flex items-center gap-2 text-sm font-semibold text-amber-400 mb-3"><TrendingUp className="w-4 h-4" /> Focus Areas</h4>
          <ul className="space-y-2">
            {(scorecard.weaknesses || []).map((s, i) => <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-amber-400">•</span>{s}</li>)}
          </ul>
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-6">
        <h4 className="text-sm font-semibold text-white mb-2">Summary</h4>
        <p className="text-sm text-slate-300 leading-relaxed">{scorecard.summary}</p>
      </div>

      {scorecard.level_assessment && (
        <div className="glass-panel rounded-2xl p-6 border border-indigo-400/20" data-testid="career-intelligence-card">
          <h4 className="flex items-center gap-2 text-sm font-semibold text-indigo-300 mb-2"><Compass className="w-4 h-4" /> AI Career Intelligence</h4>
          <p className="text-sm text-slate-200 leading-relaxed" data-testid="career-level-assessment">{scorecard.level_assessment}</p>
          {scorecard.estimated_prep_days != null && (
            <p className="text-xs text-slate-400 mt-2" data-testid="career-estimated-prep-days">Estimated preparation time to reach the next level: <span className="text-white font-semibold">{scorecard.estimated_prep_days} days</span></p>
          )}
        </div>
      )}
    </div>
  );
}
